import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DollarSign, FileText, MoreHorizontal } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import type {
  ExpenseCashFilter,
  ExpenseItemWithRelations,
  ExpenseListSort,
  User,
} from '@peles/shared';
import { isPositiveAmount, formatCurrency, isPlannerOrAbove } from '@peles/shared';
import { EntityModal } from '../common/EntityModal';
import { DropdownMenu, type DropdownMenuItem } from '../common/DropdownMenu';
import type { SortDirection } from '../common/EntityList';
import { useDialog } from '../../context/DialogContext';
import {
  useDeleteExpenseItem,
  useExpenseConfig,
  useExpenseItemPage,
} from '../../hooks/useExpenses';
import { useVendorCategories } from '../../hooks/useVendors';
import { useThreadCounts } from '../../hooks/useThreadCounts';
import { useEvents } from '../../hooks/useEvents';
import { ExpenseFilters, EMPTY_EXPENSE_FILTERS, type ExpenseFiltersState } from './ExpenseFilters';
import { ExpenseTable, EXPENSE_PAGE_SIZES } from './ExpenseTable';
import { ExpenseDetailPanel } from './ExpenseDetailPanel';
import { CreateExpensePanel } from './CreateExpensePanel';
import { DocumentImportModal } from './DocumentImportModal';
import { EventSelectorModal } from './EventSelectorModal';
import { ItemizedListModal } from '../planning/ItemizedListModal';
import { DeleteLineWithItemsModal } from './DeleteLineWithItemsModal';
import { hasItemsCount } from '../../api/expenses';
import { VendorReviewModal } from '../vendors/VendorReviewModal';
import { CashFilterChips, parseCashFilters } from './money/CashFilterChips';
import { RecordPaymentSheet } from './money/RecordPaymentSheet';

type EntityFilter = 'EVENT' | 'VENDOR';

interface ExpenseListViewProps {
  /** Pre-scope filter — locks scope and hides the all-events selector. */
  entityType?: EntityFilter;
  /** Pre-scope id (eventId when entityType='EVENT', vendorId when 'VENDOR'). */
  entityId?: string;
  /** Show the event-name column. Defaults to: hidden when entityType='EVENT'. */
  showEventColumn?: boolean;
  /** Current user — needed for admin-only actions like delete. */
  currentUser?: User;
  /**
   * An expense to open on arrival, for a link that names one.
   *
   * ⚠️ **Opened, not filtered.** Somebody following a link from a conversation
   * or an audit entry wants *that* expense, and a one-row list still asks them
   * to click it. It is applied once — closing the panel leaves it closed rather
   * than reopening on the next render.
   */
  openExpenseItemId?: string;
  /**
   * The cash filters the list opens on when the address names none — the
   * global /expenses page opens on Overdue + Due in 30 days, the weekly cash
   * call. Cleared, the address says `cash=all`.
   */
  defaultCash?: ExpenseCashFilter[];
}

type PanelState =
  | { mode: 'closed' }
  | { mode: 'edit'; expenseItemId: string }
  | { mode: 'create'; selectedEventId: string };

/**
 * Unified expense list view — same component on the standalone /expenses page,
 * the event detail page (pre-scoped by event) and the vendor detail page
 * (pre-scoped by vendor). Owns its own filters, summary bar, header buttons,
 * and modals (edit / create / import / compare).
 *
 * Filtering, sorting and paging are the server's (`GET /expenses?page=`), so
 * the rows, the count and the summary describe one set however many lines
 * there are — the list used to fetch every line and filter them in the page.
 */
export function ExpenseListView({
  entityType,
  entityId,
  showEventColumn,
  currentUser,
  openExpenseItemId,
  defaultCash,
}: ExpenseListViewProps) {
  const isEventScoped = entityType === 'EVENT' && !!entityId;
  const isVendorScoped = entityType === 'VENDOR' && !!entityId;
  const showEventCol = showEventColumn ?? !isEventScoped;

  const [panel, setPanel] = useState<PanelState>({ mode: 'closed' });
  const [importOpen, setImportOpen] = useState(false);
  const [eventSelectorFor, setEventSelectorFor] = useState<'create' | 'invoice' | null>(null);
  // Zoom into the itemized list, pre-filtered to a single expense line.
  const [itemizedForExpense, setItemizedForExpense] = useState<ExpenseItemWithRelations | null>(
    null,
  );
  const [vendorFor, setVendorFor] = useState<ExpenseItemWithRelations | null>(null);
  const [payingFor, setPayingFor] = useState<ExpenseItemWithRelations | null>(null);
  // The cash filter is in the address (`?cash=`), so the Plan's figures can
  // link straight to it and a filtered list can be shared.
  const [params, setParams] = useSearchParams();
  const cashParam = params.get('cash');
  const cash = cashParam === null ? (defaultCash ?? []) : parseCashFilters(cashParam);
  const [filters, setFiltersState] = useState<ExpenseFiltersState>(EMPTY_EXPENSE_FILTERS);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(EXPENSE_PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ by: ExpenseListSort | null; dir: SortDirection | null }>({
    by: null,
    dir: null,
  });
  const hasUnsavedRef = useRef(false);
  const { confirm } = useDialog();

  // A new filter is a new set: start again from its first page.
  const setFilters = useCallback((next: ExpenseFiltersState) => {
    setFiltersState(next);
    setPage(1);
  }, []);
  const setCash = (next: ExpenseCashFilter[]) => {
    setParams(
      (current) => {
        const p = new URLSearchParams(current);
        p.set('cash', next.length ? next.join(',') : 'all');
        return p;
      },
      { replace: true },
    );
    setPage(1);
  };

  // ─── Effective scope ────────────
  // The route's event wins; otherwise one event picked in the filter.
  const effectiveEventId = isEventScoped
    ? entityId
    : filters.eventIds.length === 1
      ? filters.eventIds[0]
      : undefined;

  // ─── Data ────────────────────────
  const { data, isLoading } = useExpenseItemPage({
    page,
    pageSize,
    eventIds: isEventScoped ? [entityId] : filters.eventIds,
    vendorId: isVendorScoped ? entityId : undefined,
    categoryIds: filters.vendorCategoryIds,
    statusIds: filters.statusIds,
    q: filters.search,
    sort: sort.by ?? undefined,
    dir: sort.dir ?? undefined,
    // Booked lines: a need still being sourced is on the Plan view, with its quotes.
    phase: 'BOOKED',
    cash,
  });
  const items = useMemo(() => data?.items ?? [], [data]);
  const total = data?.total ?? 0;
  // Totals of every line the filter matches, from the server — not only the
  // page on screen. Open balance is each line's positive balance: an overpaid
  // line owes nothing and cancels nothing.
  const summary = data?.summary ?? {
    count: 0,
    totalExpenses: 0,
    totalPaid: 0,
    openBalance: 0,
    overdue: 0,
    dueSoon: 0,
  };
  // The item being paid, as it stands now (a refetch after paying updates it).
  const paying = payingFor ? (items.find((i) => i.id === payingFor.id) ?? payingFor) : null;
  const { data: config } = useExpenseConfig();
  const { data: categories = [] } = useVendorCategories();
  const { data: allEvents = [] } = useEvents();
  const deleteExpenseItem = useDeleteExpenseItem();

  // A page past the end (the last line on it deleted, or a narrower filter
  // elsewhere) steps back to the last page there is.
  useEffect(() => {
    const lastPage = Math.max(1, Math.ceil(total / pageSize));
    if (data && page > lastPage) setPage(lastPage);
  }, [data, total, page, pageSize]);

  // ─── Unsaved-edit guard ──────────
  const confirmUnsaved = useCallback(async (): Promise<boolean> => {
    if (hasUnsavedRef.current) {
      return confirm({
        title: 'Unsaved Changes',
        message: 'You have unsaved changes. Discard them?',
        confirmLabel: 'Discard',
        variant: 'warning',
      });
    }
    return true;
  }, [confirm]);

  const handleSelectExpense = useCallback(
    async (id: string) => {
      if (!(await confirmUnsaved())) return;
      hasUnsavedRef.current = false;
      setPanel({ mode: 'edit', expenseItemId: id });
    },
    [confirmUnsaved],
  );

  const handleClosePanel = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    setPanel({ mode: 'closed' });
  }, [confirmUnsaved]);

  // Applied once per id. Without the ref, closing the panel would re-run this
  // effect's condition on the next render and reopen what was just dismissed.
  const openedFromLink = useRef<string | null>(null);
  useEffect(() => {
    if (!openExpenseItemId || openedFromLink.current === openExpenseItemId) return;
    openedFromLink.current = openExpenseItemId;
    setPanel({ mode: 'edit', expenseItemId: openExpenseItemId });
  }, [openExpenseItemId]);

  const handleUnsavedChange = useCallback((dirty: boolean) => {
    hasUnsavedRef.current = dirty;
  }, []);

  // A line with items is refused (409) until it is said where they go.
  const [deletingWithItems, setDeletingWithItems] = useState<{
    line: ExpenseItemWithRelations;
    itemCount: number;
  } | null>(null);
  const handleDelete = useCallback(
    (id: string) => {
      deleteExpenseItem.mutate(id, {
        onError: (err) => {
          const itemCount = hasItemsCount(err);
          const line = items.find((i) => i.id === id);
          if (itemCount !== null && line) setDeletingWithItems({ line, itemCount });
        },
      });
    },
    [deleteExpenseItem, items],
  );

  // ─── Header actions ──────────────
  const handleCreateClick = useCallback(async () => {
    if (!(await confirmUnsaved())) return;
    hasUnsavedRef.current = false;
    if (isEventScoped && entityId) {
      setPanel({ mode: 'create', selectedEventId: entityId });
    } else {
      setEventSelectorFor('create');
    }
  }, [confirmUnsaved, isEventScoped, entityId]);

  const handleImportInvoiceClick = useCallback(() => {
    if (isEventScoped && entityId) {
      setImportOpen(true);
    } else {
      setEventSelectorFor('invoice');
    }
  }, [isEventScoped, entityId]);

  const handleEventSelected = useCallback(
    (selectedId: string) => {
      const which = eventSelectorFor;
      setEventSelectorFor(null);
      if (which === 'create') {
        setPanel({ mode: 'create', selectedEventId: selectedId });
      } else if (which === 'invoice') {
        // Show the event the lines are imported into.
        setFilters({ ...filters, eventIds: [selectedId] });
        setImportOpen(true);
      }
    },
    [eventSelectorFor, filters, setFilters],
  );

  const handleCreated = useCallback(() => {
    hasUnsavedRef.current = false;
    setPanel({ mode: 'closed' });
  }, []);

  const importEventId = effectiveEventId;
  /*
   * One request for every row's badge, rather than one per row — for the page
   * on screen, so paging asks about the next page and re-sorting about
   * nothing new (the hook sorts the key).
   */
  const { countFor } = useThreadCounts(
    'ExpenseItem',
    items.map((item) => item.id),
  );

  // One labelled primary action; the rest in "More", so the header never
  // overflows a phone.
  const moreActions: DropdownMenuItem[] = [
    {
      id: 'import-invoice',
      label: 'Import invoice',
      icon: <FileText size={14} />,
      onSelect: handleImportInvoiceClick,
    },
  ];

  const editingId = panel.mode === 'edit' ? panel.expenseItemId : null;
  const creatingForEventId = panel.mode === 'create' ? panel.selectedEventId : null;

  return (
    <>
      {/* Header — title-less in embedded mode (parent shows the section header) */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-semibold text-gray-900">Expenses</h3>
        <div className="flex items-center gap-2">
          <DropdownMenu
            ariaLabel="More expense actions"
            align="end"
            items={moreActions}
            trigger={(props) => (
              <button
                type="button"
                {...props}
                className="min-h-11 flex items-center gap-2 px-3 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
              >
                <MoreHorizontal size={16} />
                More
              </button>
            )}
          />
          <button
            type="button"
            onClick={handleCreateClick}
            className="min-h-11 flex items-center gap-2 px-3 bg-primary-600 text-white text-sm rounded-md hover:bg-primary-700"
          >
            <DollarSign size={16} />
            Add Expense
          </button>
        </div>
      </div>

      {/* Summary Bar */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-4">
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Total Expenses</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(summary.totalExpenses)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Total Paid</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(summary.totalPaid)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3 col-span-2 sm:col-span-1">
          <div className="text-xs text-gray-500">Open Balance</div>
          <div
            className={`text-lg font-semibold ${isPositiveAmount(summary.openBalance) ? 'text-red-600' : 'text-green-600'}`}
          >
            {formatCurrency(summary.openBalance)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Due in 30 days</div>
          <div className="text-lg font-semibold text-gray-900">
            {formatCurrency(summary.dueSoon)}
          </div>
        </div>
        <div className="bg-white rounded-lg shadow px-4 py-3">
          <div className="text-xs text-gray-500">Overdue</div>
          <div
            className={`text-lg font-semibold ${isPositiveAmount(summary.overdue) ? 'text-red-600' : 'text-gray-900'}`}
          >
            {formatCurrency(summary.overdue)}
          </div>
        </div>
      </div>

      <div className="mb-3">
        <CashFilterChips value={cash} onChange={setCash} />
      </div>

      {/* Filters */}
      <ExpenseFilters
        filters={filters}
        onFiltersChange={setFilters}
        categories={categories}
        statuses={config?.statuses ?? []}
        events={isEventScoped || isVendorScoped ? undefined : allEvents}
      />

      {/* Table */}
      <ExpenseTable
        items={items}
        selectedId={editingId}
        onSelect={handleSelectExpense}
        onDelete={handleDelete}
        onZoomItems={setItemizedForExpense}
        onVendorClick={setVendorFor}
        onRecordPayment={setPayingFor}
        isLoading={isLoading}
        currentUser={currentUser}
        showEventName={showEventCol}
        threadCountFor={countFor}
        page={page}
        pageSize={pageSize}
        total={total}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        sortBy={sort.by}
        sortDir={sort.dir}
        onSortChange={(by, dir) => {
          setSort({ by, dir });
          setPage(1);
        }}
      />

      {/* Edit modal */}
      <EntityModal
        open={editingId !== null}
        onClose={handleClosePanel}
        title="Expense Details"
        size="xl"
      >
        {editingId && currentUser && (
          <ExpenseDetailPanel
            key={editingId}
            expenseItemId={editingId}
            onClose={handleClosePanel}
            onUnsavedChange={handleUnsavedChange}
            currentUser={currentUser}
          />
        )}
      </EntityModal>

      {/* Create modal */}
      <EntityModal
        open={creatingForEventId !== null}
        onClose={handleClosePanel}
        title="Add Expense"
        size="lg"
      >
        {creatingForEventId && (
          <CreateExpensePanel
            eventId={creatingForEventId}
            onClose={handleClosePanel}
            onCreated={handleCreated}
            onUnsavedChange={handleUnsavedChange}
          />
        )}
      </EntityModal>

      {/* Import modals */}
      {importEventId && importOpen && (
        <DocumentImportModal
          eventId={importEventId}
          isOpen
          onClose={() => setImportOpen(false)}
          onImported={() => setImportOpen(false)}
        />
      )}

      {/* Itemized list zoom — pre-filtered to the clicked expense line */}
      {itemizedForExpense && (
        <ItemizedListModal
          open
          onClose={() => setItemizedForExpense(null)}
          eventId={itemizedForExpense.eventId}
          initialFilters={{ expenseItemIds: [itemizedForExpense.id] }}
          title={`Itemized List — ${itemizedForExpense.itemName ?? itemizedForExpense.vendorCategoryName}`}
        />
      )}

      <RecordPaymentSheet item={paying} onClose={() => setPayingFor(null)} />
      <DeleteLineWithItemsModal
        line={deletingWithItems?.line ?? null}
        itemCount={deletingWithItems?.itemCount ?? 0}
        onClose={() => setDeletingWithItems(null)}
      />

      {/* The vendor named on a line — its contacts, and how to pay the line's balance */}
      <VendorReviewModal
        open={vendorFor !== null}
        vendorId={vendorFor?.vendorId ?? null}
        onClose={() => setVendorFor(null)}
        canManagePayments={!!currentUser && isPlannerOrAbove(currentUser.systemRole)}
        payment={
          vendorFor
            ? {
                amount: vendorFor.balance,
                label: `Open balance on ${vendorFor.itemName || vendorFor.vendorCategoryName}`,
                note: [vendorFor.eventName, vendorFor.itemName || vendorFor.vendorCategoryName]
                  .filter(Boolean)
                  .join(' — '),
              }
            : undefined
        }
      />

      <EventSelectorModal
        isOpen={eventSelectorFor !== null}
        onClose={() => setEventSelectorFor(null)}
        onSelect={handleEventSelected}
        title={
          eventSelectorFor === 'create'
            ? 'Select Event for New Expense'
            : 'Select Event for Invoice Import'
        }
      />
    </>
  );
}
